import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { trackEvent, setTrackingIdentity } from './tracking';

type StaffRow = {
  user_id: string;
  display_name: string;
  role: 'admin' | 'sales' | 'ops' | 'finance' | 'marketing';
  active: boolean;
};

type AuthCtx = {
  session: Session | null;
  user: User | null;
  staff: StaffRow | null;
  loading: boolean;
  signInWithEmail: (email: string) => Promise<{ error: Error | null }>;
  signInWithPassword: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [staff, setStaff] = useState<StaffRow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, sess) => {
      if (event === 'SIGNED_OUT') {
        // Puede ser rotación de refresh token entre pestañas: otra pestaña pudo refrescar
        // y dejar una sesión válida en storage. Re-verifica antes de tirar la sesión (evita logout falso).
        setTimeout(() => {
          supabase.auth.getSession().then(({ data }) => setSession(data.session ?? null));
        }, 500);
        return;
      }
      setSession(sess);
      // Tracking: primer acceso real al portal. La llave única evita contar
      // refreshes, pestañas nuevas y logins posteriores como otro lead.
      if (event === 'SIGNED_IN' && sess?.user) {
        void (async () => {
          const { data: membership } = await supabase
            .from('customer_users')
            .select('customer_id')
            .eq('user_id', sess.user.id)
            .maybeSingle();
          await trackEvent('lead_portal', {
            source: 'portal_login',
          }, undefined, {
            userId: sess.user.id,
            customerId: membership?.customer_id ?? null,
            dedupeKey: `lead_portal:${sess.user.id}`,
          });
        })();
      }
    });
    return () => { sub.subscription.unsubscribe(); };
  }, []);

  // Liga la actividad del sitio al cliente con sesión (para el CRM).
  useEffect(() => {
    let alive = true;
    const uid = session?.user?.id ?? null;
    if (!uid) { setTrackingIdentity(null, null); return; }
    setTrackingIdentity(uid, null);
    supabase.from('customer_users').select('customer_id').eq('user_id', uid).limit(1).maybeSingle()
      .then(({ data }) => { if (alive) setTrackingIdentity(uid, (data as { customer_id: string } | null)?.customer_id ?? null); });
    return () => { alive = false; };
  }, [session?.user?.id]);

  useEffect(() => {
    let alive = true;
    if (!session?.user) { setStaff(null); return; }
    supabase
      .from('staff')
      .select('user_id, display_name, role, active')
      .eq('user_id', session.user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (alive) setStaff((data as StaffRow | null) ?? null);
      });
    return () => { alive = false; };
  }, [session]);

  const signInWithEmail = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin + '/portal' },
    });
    return { error };
  };

  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signOut = async () => { await supabase.auth.signOut(); };

  return (
    <Ctx.Provider value={{
      session,
      user: session?.user ?? null,
      staff,
      loading,
      signInWithEmail,
      signInWithPassword,
      signOut,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth fuera de AuthProvider');
  return v;
}
